import {
  faCircleArrowDown,
  faCircleArrowUp,
  faPlus,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

import { Area } from "~/components/charts/area";
import { BlockchainIcon } from "~/components/logos/blockchain";
import { TokenLogo } from "~/components/logos/token";
import { Error } from "~/components/site/error";
import { item } from "~/components/site/layout";
import { AnalyticsNav, Page } from "~/components/stablecoins/analytics-nav";
import { BridgedPopover } from "~/components/stablecoins/bridged-popover";
import { Layout } from "~/components/stablecoins/layout";
import { Percent } from "~/components/stablecoins/percent";
import { SummaryCard } from "~/components/stablecoins/summary-card";
import { SummaryCardDescription } from "~/components/stablecoins/summary-card-description";
import { abbreviatedNum } from "~/lib/abbreviated-num";
import { toDateString } from "~/lib/charts";
import { ROUTE_DATA } from "~/lib/constants";
import { getMeta } from "~/lib/get-meta";
import { getUrls } from "~/lib/get-urls";
import { referralLink } from "~/lib/referral-link";
import type { BlockchainId, RollupData } from "~/lib/stablecoins.server";

const { desc, path, title, ogImage } = ROUTE_DATA.stablecoins;

const BRALE_CHAINS: BlockchainId[] = [
  "arbitrum",
  "avalanche",
  "base",
  "canton",
  "celo",
  "coreum",
  "ethereum",
  "ethereum-classic",
  "hedera",
  "optimism",
  "polygon",
  "solana",
  "stellar",
];

export function loader({ request }: LoaderFunctionArgs) {
  return {
    urls: getUrls({ path, request }),
  };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return data
    ? [
        ...getMeta({ desc, title, urls: data.urls }),
        { property: "og:image", content: `${data.urls.base}${ogImage}` },
        { name: "twitter:image", content: `${data.urls.base}${ogImage}` },
      ]
    : [];
};

export default function Rollup(): JSX.Element {
  const [data, setData] = useState<RollupData>();

  useEffect(() => {
    async function getData() {
      setData(
        await (await fetch("https://app.brale.xyz/api/stablecoins")).json()
      );
    }

    if (data == null) getData();
  }, [data]);

  return (
    <Layout>
      <motion.div
        variants={item}
        className="mb-16 flex flex-col gap-y-1 prose dark:prose-invert"
      >
        <h1 className="h2 mb-6">Top Stablecoins by Market Cap</h1>
        <p className="subheading m-0">
          Market analytics for transparent, fiat-backed stablecoins. Only native
          supply is considered.
        </p>
      </motion.div>
      <motion.div variants={item}>
        <AnalyticsNav page={Page.STABLECOINS} />
      </motion.div>

      <motion.div
        variants={item}
        className="grid grid-rows-1 sm:grid-cols-3 mt-8 gap-x-6 gap-y-4"
      >
        <SummaryCard
          description={<SummaryCardDescription title="Total Market Cap" />}
        >
          {data?.total_mcap && (
            <div className="flex gap-x-2">
              <span>{`$${abbreviatedNum(data.total_mcap)}`}</span>
              <span className="text-secondary">USD</span>
            </div>
          )}
        </SummaryCard>
        <SummaryCard description="Change (1m)">
          <div className="flex items-center gap-x-2">
            {data?.mcap_30d_change && data.mcap_30d_change > 0 && (
              <FontAwesomeIcon
                icon={faCircleArrowUp}
                className="text-green-500"
              />
            )}
            {data?.mcap_30d_change && data.mcap_30d_change < 0 && (
              <FontAwesomeIcon
                icon={faCircleArrowDown}
                className="text-red-500"
              />
            )}

            {data?.mcap_30d_change && <Percent value={data?.mcap_30d_change} />}
          </div>
        </SummaryCard>
        <SummaryCard description="Dominance">
          <div className="flex items-center gap-x-2">
            {data?.dominant_token && <TokenLogo ticker={data.dominant_token} />}
            {data?.dominant_token && (
              <span className="sm:hidden md:inline-block">
                {data.dominant_token}:
              </span>
            )}
            {data?.dominant_percentage && (
              <Percent colored={false} value={data?.dominant_percentage} />
            )}
          </div>
        </SummaryCard>
      </motion.div>
      <motion.div variants={item} className="relative mt-6 h-[30vh] w-full">
        {data && (
          <Area
            labels={data.chart.dates.map(toDateString)}
            options={{ maintainAspectRatio: false }}
            values={data.chart.values}
            reserveCurrency="USD"
          />
        )}
      </motion.div>
      <motion.div variants={item} className="mt-6 sm:mt-12 mb-16">
        <div className="border border-primary h-min round overflow-hidden relative">
          <table className="min-w-full divide-y divide-primary text-black dark:text-white">
            <thead className="bg-gray-100 dark:bg-gray-900">
              <tr>
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-sm sm:pl-6 font-normal"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="hidden px-3 py-3.5 text-left text-sm sm:table-cell font-normal"
                >
                  <span className="flex items-center justify-start gap-x-2">
                    Chains{" "}
                    <BridgedPopover>
                      {" "}
                      Shows chains natively supported by the stablecoin only.
                      Wrapped stablecoins are not included.
                    </BridgedPopover>
                  </span>
                </th>
                <th
                  scope="col"
                  className="hidden px-3 py-3.5 text-left text-sm lg:table-cell font-normal"
                >
                  Price
                </th>
                <th
                  scope="col"
                  className="hidden px-3 py-3.5 text-right text-sm lg:table-cell font-normal"
                >
                  <span className="flex items-center justify-end gap-x-2">
                    1m % Off Peg{" "}
                    <BridgedPopover>
                      {" "}
                      Shows largest occurrence of price change from $1 peg in
                      the past month.
                    </BridgedPopover>
                  </span>
                </th>
                {/** 
                <th
                  scope="col"
                  className="hidden px-3 py-3.5 text-right text-sm lg:table-cell font-normal"
                >
                  1D Change
                </th>
                */}
                <th
                  scope="col"
                  className="hidden px-3 py-3.5 text-right text-sm lg:table-cell font-normal"
                >
                  7d Change
                </th>
                <th
                  scope="col"
                  className="hidden px-3 py-3.5 text-right text-sm lg:table-cell font-normal"
                >
                  1m Change
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-right text-sm font-normal whitespace-nowrap lg:whitespace-normal"
                >
                  Market Cap
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-primary dark:divide-gray-700 bg-white dark:bg-black">
              {data?.tokens
                .filter((t) => t.symbol !== "rUSD")
                .map((t) => (
                  <tr key={t.symbol}>
                    <td className="w-full max-w-0 py-4 pl-4 pr-3 text-sm sm:w-auto sm:max-w-none sm:pl-6">
                      <a
                        href={`/stablecoins/${t.symbol}`}
                        className="flex items-center gap-x-2"
                      >
                        <TokenLogo ticker={t.symbol} />
                        <div className="flex flex-col min-[400px]:flex-row sm:flex-col md:flex-row lg:flex-col xl:flex-row gap-x-2">
                          <span className="font-semibold">{t.symbol}</span>
                          <span className="text-secondary">{t.name}</span>
                        </div>
                      </a>
                    </td>
                    <td className="hidden px-3 py-4 text-sm sm:table-cell text-right">
                      <ul className="flex items-center">
                        {t.chains.map((c, i) => (
                          <li
                            key={c}
                            title={c}
                            className={`${i !== 0 ? "-ml-1" : ""}`}
                          >
                            <BlockchainIcon
                              className="h-6 w-6 rounded-full"
                              chainId={
                                c
                                  .replace(" ", "-")
                                  .toLowerCase() as BlockchainId
                              }
                            />
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="hidden px-3 py-4 text-sm lg:flex lg:flex-col xl:flex-row gap-x-2">
                      <span>${t.price.toFixed(3)}</span>{" "}
                      <span className="text-secondary">
                        {t.reserve_currency}
                      </span>
                    </td>
                    <td className="hidden px-3 py-4 text-sm lg:table-cell text-right">
                      <Percent value={t.peg_deviation_1m} />
                    </td>
                    {/**
                    <td className="hidden px-3 py-4 text-sm lg:table-cell text-right">
                      <Percent value={t.change_1d} />
                    </td>
                    */}
                    <td className="hidden px-3 py-4 text-sm lg:table-cell text-right">
                      <Percent value={t.change_7d} />
                    </td>
                    <td className="hidden px-3 py-4 text-sm lg:table-cell text-right">
                      <Percent value={t.change_1m} />
                    </td>
                    <td className="flex flex-col min-[400px]:flex-row sm:flex-col md:flex-row lg:flex-col xl:flex-row gap-x-2 px-3 py-4 text-sm justify-end">
                      <span className="text-right">
                        {t.mcap && t.mcap < 1000000
                          ? "< $1M"
                          : `$${abbreviatedNum(t.mcap)}`}
                      </span>{" "}
                      <span className="text-secondary text-right">
                        {t.reserve_currency}
                      </span>
                    </td>
                  </tr>
                ))}
              <tr className="relative">
                <td className="w-full max-w-0 py-4 pl-4 pr-3 text-sm sm:w-auto sm:max-w-none sm:pl-6">
                  <span className="flex items-center gap-x-2">
                    <TokenLogo ticker="default" />
                    <div className="flex flex-col min-[400px]:flex-row sm:flex-col md:flex-row lg:flex-col xl:flex-row gap-x-2">
                      <span className="font-semibold">YSBC</span>
                      <span className="text-secondary">Your Stablecoin</span>
                    </div>
                  </span>
                </td>
                <td className="hidden px-3 py-4 text-sm sm:table-cell text-right">
                  <ul className="flex items-center">
                    {BRALE_CHAINS.sort().map((c, i) => (
                      <li
                        className={`${i !== 0 ? "-ml-1" : ""}`}
                        key={c}
                        title={c}
                      >
                        <BlockchainIcon
                          chainId={c}
                          className="h-6 w-6 rounded-full"
                        />
                      </li>
                    ))}
                  </ul>
                </td>
                <td colSpan={6} className="text-center">
                  <span className="flex items-center justify-center">
                    <a
                      className="btn primary"
                      href={referralLink({ seoRef: "create-stablecoin" })}
                    >
                      <FontAwesomeIcon
                        className="h-5 w-5 md:mr-2"
                        icon={faPlus}
                      />
                      <span className="hidden md:inline">
                        Create stablecoin
                      </span>
                    </a>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </motion.div>
    </Layout>
  );
}

export function CatchBoundary() {
  return <Error statusCode={404} themed />;
}
